import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Theme,
  IconButton,
  useTheme,
  useMediaQuery,
  Link,
  SxProps,
  Skeleton,
  Stack,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import { FormType } from "models/FormType";
import { AtticusForm } from "models/AtticusForm";
import { ProbateType } from "models/ProbateType";
import { User } from "models/User";
import { Country, State } from "models/Geography";
import { Estate } from "models/Estate";

import {
  SplitButtonGroup,
  SplitButtonOption,
} from "views/shared/SplitButtonGroup";
import { FormThumbnail } from "../FormThumbnail";
import { useAuthContext } from "AuthContextWrapper"
import { atticusColors } from "@theatticusapp/atticus-shared-components";
import { EstateFormsContext } from "EstateFormsContextWrapper";

interface Props {
  form?: AtticusForm;
  formTypes?: FormType[];
  estate?: Estate;
  probateTypes?: ProbateType[];
  states?: State[];
  pdfDialogOpen: boolean;
  setPdfDialogForm: (form: AtticusForm) => void;
  togglePdfDialog: () => void;
  descriptionDialogOpen: boolean;
  setDescriptionDialogForm: (form: AtticusForm) => void;
  toggleDescriptionDialog: () => void;
  sx?: SxProps<Theme>;
  formOptions?: SplitButtonOption[];
  checkedBadge?: boolean;
}

function stringAvatar(name?: string) {
  const nameParts = name?.split(" ")
  if(nameParts && nameParts.length > 1 && nameParts[0].length > 0 && nameParts[1].length > 0) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
  }

  return  undefined;
}

export const FormsListItemCard = ({
  form,
  setPdfDialogForm,
  togglePdfDialog,
  descriptionDialogOpen,
  setDescriptionDialogForm,
  toggleDescriptionDialog,
  formOptions,
  sx,
  checkedBadge = false,
}: Props): JSX.Element => {
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const { formTypes, user, coreDataIsLoading } = useAuthContext()
  const { estateFormsIsLoading } = useContext(EstateFormsContext);
  const [formType, setFormType] = useState<FormType>();
  const stackFontSize = isMobileSize ? "0.75rem" : "1rem";
  const isLoading =
    estateFormsIsLoading ||
    coreDataIsLoading ||
    !(form?.formTypeId || formType);

  useEffect(() => {
    setFormType(
      formTypes?.find((formType: FormType) => formType.id === form?.formTypeId)
    );
  }, [formTypes]);

  const getSourcePuck = (source: ProbateType | User): JSX.Element => {
    let text = undefined;
    let avatarAlt = undefined;
    let avatarSrc = undefined;
    let avatarText = undefined;
    let avatarBgColor = undefined;
    const avatarTextColor = atticusColors.purple5;
    let textColor = undefined;

    switch (true) {
      case source instanceof ProbateType:
        source = source as ProbateType;
        text = `Included in ${source.stateCode} ${source.name}`;
        avatarAlt = "Atticus Logo on top of a purple background";
        avatarSrc = "/static/images/atticus-a.svg";
        avatarBgColor = atticusColors.purple4;
        textColor = "secondary.main";
        break;
      case source instanceof User:
        source = source as User;
        avatarText = stringAvatar(source.fullName);
        avatarBgColor = atticusColors.purple2;
        text = `Added by ${source.id == user?.id ? "you" : source.fullName}`;
        textColor = "text.primary";
        break;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5rem",
          flex: 1,
        }}
      >
        <>
          <Avatar
            src={avatarSrc}
            alt={avatarAlt}
            sx={{
              height: "1.5rem",
              width: "1.5rem",
              bgcolor: avatarBgColor,
              color: avatarTextColor,
              fontSize: "0.8rem",
              fontWeight: "800",
              "& img": {
                width: "50%",
                height: "50%",
              },
            }}
          >
            {avatarText}
          </Avatar>

          <Typography
            variant="body1"
            color={textColor}
            sx={{ fontSize: stackFontSize }}
          >
            {text}
          </Typography>
        </>
      </Box>
    );
  };

  return (
    <Card variant="outlined" sx={sx}>
      <CardContent sx={{ padding: "0 !IMPORTANT" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 0,
          }}
        >
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: "5rem",
                width: "3.875rem",
              }}
            />
          ) : (
            <>
              <FormThumbnail
                form={form}
                onClick={async () => {
                  if (form) {
                    setPdfDialogForm(form);
                    togglePdfDialog();
                  }
                }}
                sx={{
                  width: "3.875rem",
                  alignItems: isMobileSize ? "baseline" : "center",
                }}
                checkedBadge={checkedBadge}
              />
            </>
          )}
          <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box
                sx={(theme: Theme) => ({
                  color: theme.palette.primary.dark,
                  marginBottom: "0.25rem",
                  display: "flex",
                  gap: "0.5rem",
                })}
              >
                {isLoading ? (
                  <Skeleton
                    variant="text"
                    sx={{
                      width: "90%",
                    }}
                  />
                ) : (
                  <>
                    <Link
                      sx={{
                        fontWeight: "700",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: "3",
                        WebkitBoxOrient: "vertical",
                        flex: isMobileSize ? 1 : undefined,
                        "&:hover": { color: "text.primary" },
                      }}
                      underline="hover"
                      color="text.primary"
                      onClick={async () => {
                        if (form) {
                          setPdfDialogForm(form);
                          togglePdfDialog();
                        }
                      }}
                    >
                      {form?.formTitle}
                    </Link>
                    {form?.formDescription && (
                      <IconButton
                        size="small"
                        disabled={descriptionDialogOpen}
                        onClick={async () => {
                          setDescriptionDialogForm(form);
                          toggleDescriptionDialog();
                        }}
                        sx={{
                          height: "fit-content",
                          marginTop: "-5px",
                          color: "text.primary",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        <HelpIcon />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
              <Stack
                direction="row"
                gap={isMobileSize ? 1 : 2}
                flexWrap={isMobileSize ? "wrap" : undefined}
              >
                {isLoading ? (
                  <Skeleton variant="text" sx={{ width: "15%" }} />
                ) : (
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{
                      fontSize: stackFontSize,
                    }}
                  >
                    {form?.geography instanceof Country ||
                    form?.geography instanceof State
                      ? form?.geography.code
                      : form?.geography?.name || ""}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant="text" sx={{ width: "15%" }} />
                ) : (
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{
                      fontSize: stackFontSize,
                    }}
                  >
                    {`${
                      form?.formType?.name ||
                      formTypes?.find(
                        (ft: FormType) => ft.id == form?.formTypeId
                      )?.name || ""
                    }`}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant="text" sx={{ width: "15%" }} />
                ) : (
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{
                      fontSize: stackFontSize,
                    }}
                  >
                    {form?.formGovernmentId}
                  </Typography>
                )}
                {form?.estateFormMap?.source && (
                  <>
                    {isLoading ? (
                      <Skeleton variant="text" sx={{ width: "30%" }} />
                    ) : (
                      getSourcePuck(form.estateFormMap.source)
                    )}
                  </>
                )}
              </Stack>
            </Box>
            {!isLoading && formOptions && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SplitButtonGroup
                  color="primary"
                  options={formOptions}
                  variant="contained"
                  popperPlacement="bottom-end"
                  sx={{
                    maxWidth: "12rem",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
